import {
  ComponentIds,
  Entity,
  HostConnectionsComponent,
  ProductUtils,
  SelectedComponent
} from '@inter-ikea-core/core';
import { Product, ProductType, SelectionOverrideComponent } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import {
  getFilterState,
  getMarketProducts,
  getSelectedGroupId,
  getShoppingItemsInScene
} from 'ducks/products';
import { setShowSelectionOverrideIndicators } from 'ducks/selectionOverride';
import addJonaxelInteriorSuggestion from 'rangeConfig/jonaxel/behaviors/addJonaxelInteriorSuggestion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isMobile } from 'utils/platform';
import InteriorSuggestionsDesktop from '../../shared/widgets/interiorSuggestions/InteriorSuggestionsDesktop';
import InteriorSuggestionsMobile from '../../shared/widgets/interiorSuggestions/InteriorSuggestionsMobile';
import SelectInSceneImage from './Images/InteriorSuggestionsSelectInScene.png';
import { store } from 'ducks';
import { useBehaviorTracker } from 'contexts/behaviors';
import { noneOptionForInteriorSuggestions } from './noneOptionForInteriorSuggestions';
import { hasOutsideClothRailOnTop } from '@inter-ikea-storageone/range-jonaxel/build/utils/clothRailUtils';

const frameTypes = [ProductType.JONAXEL_FRAME];
const interiorSuggestionTypes = [ProductType.JONAXEL_INTERIOR_SUGGESTION];

export default function InteriorSuggestions() {
  const dispatch = useAppDispatch();
  const rootState = store.getState();
  const products = useSelector(getMarketProducts);
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();
  const entityManager = coreApi.getServices().entityManager;
  const filterState = useSelector(getFilterState);
  const selectedGroupId = useSelector(getSelectedGroupId);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [interiorSuggestions, setInteriorSuggestions] = useState<Product[]>([]);
  const productsInScene = useSelector(getShoppingItemsInScene);

  const interiorSuggestionProducts = useMemo(() => {
    return products.filter(p => interiorSuggestionTypes.includes(p.type));
  }, [products]);

  const frameProducts = useMemo(() => {
    return products.filter(p => frameTypes.includes(p.type));
  }, [products]);

  const optionsMap: Record<string, ProductType[]> = filterState(selectedGroupId, 'type');

  const selectedOptionsTypes = Object.values(optionsMap).filter(o => o.length > 0);

  useEffect(() => {
    return coreApi.subscribe('history', updateInteriorSuggestions);
  });

  useEffect(() => {
    updateInteriorSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  const updateInteriorSuggestions = () => {
    if (selectedEntity) {
      const frameProduct = frameProducts.find(
        p => p.coreProductId === ProductUtils.productRef(selectedEntity)
      );

      const hasFrameOnTop = selectedEntity
        .getComponent<HostConnectionsComponent>(ComponentIds.HOST_CONNECTIONS_COMPONENT_ID)
        ?.connections.some(c => c.hostConnector.key.includes('stacking-host'));

      const outsideClothRailOnTop = hasOutsideClothRailOnTop(coreApi, selectedEntity);

      let suggestionsForSelectedFrame = interiorSuggestionProducts.filter(p => {
        return frameProduct
          ? p.communicatedDimensions.width === frameProduct.communicatedDimensions.width &&
              p.communicatedDimensions.depth === frameProduct.communicatedDimensions.depth &&
              p.communicatedDimensions.height === frameProduct.communicatedDimensions.height &&
              p.design === (filterState(selectedGroupId, 'design').design || frameProduct.design) &&
              (!hasFrameOnTop ||
                !p.parts.some(part => part.product?.type === ProductType.JONAXEL_TOP_SHELF)) &&
              (!outsideClothRailOnTop ||
                !p.parts.some(
                  part => part.product?.type === ProductType.JONAXEL_CLOTH_RAIL_ADJUSTABLE
                ))
          : false;
      });

      suggestionsForSelectedFrame = suggestionsForSelectedFrame.filter(product => {
        const partTypes = product.parts.flatMap(part => part.product?.type || []);
        const allOptionsSatisfied = selectedOptionsTypes.every(selectedTypes =>
          selectedTypes.find(type => partTypes.includes(type))
        );
        return allOptionsSatisfied;
      });

      setInteriorSuggestions(suggestionsForSelectedFrame);
    }
  };

  const addSelectionOverrideComponentToFrames = useCallback(() => {
    const framesInScene = coreApi.entities
      .getByComponent(ComponentIds.FRAME_COMPONENT_ID)
      .filter(e => coreApi.entities.isProductType(e, ProductType.JONAXEL_FRAME));

    framesInScene.forEach(frame => frame.addComponent(new SelectionOverrideComponent()));
    if (framesInScene.length === 1) {
      // If only one frame in scene, select it
      framesInScene[0].addComponent(new SelectedComponent());
      setSelectedEntity(framesInScene[0]);
    }
  }, [coreApi]);

  const removeSelectionOverrideComponentFromFrames = useCallback(() => {
    const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
    framesInScene.forEach(frame => frame.removeComponent(SelectionOverrideComponent.id));
  }, [coreApi]);

  // Add selection override component to frames when products in scene changes.
  useEffect(() => {
    addSelectionOverrideComponentToFrames();
    dispatch(setShowSelectionOverrideIndicators(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsInScene]);

  useEffect(() => {
    addSelectionOverrideComponentToFrames();
    dispatch(setShowSelectionOverrideIndicators(true));
    return () => {
      removeSelectionOverrideComponentFromFrames();
      dispatch(setShowSelectionOverrideIndicators(false));
      coreApi.entities.unselect();
    };
  }, [
    addSelectionOverrideComponentToFrames,
    coreApi,
    dispatch,
    removeSelectionOverrideComponentFromFrames
  ]);

  useEffect(() => {
    const onSelectedEntity = (entity: Entity) => {
      if (entity.hasComponent(SelectionOverrideComponent.id)) {
        setSelectedEntity(entity);
      }
    };
    const onUnSelectedEntity = (entity: Entity) => {
      if (entity.hasComponent(SelectionOverrideComponent.id)) {
        setSelectedEntity(null);
      }
    };

    entityManager.subscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, onSelectedEntity);
    entityManager.subscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, onUnSelectedEntity);
    return () => {
      entityManager.unsubscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, onSelectedEntity);
      entityManager.unsubscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, onUnSelectedEntity);
    };
  }, [entityManager]);

  const handleClick = (product: Product) =>
    behaviorTracker.custom(() => {
      const props = {
        coreProductId: product.coreProductId,
        menu: 'main_menu'
      };
      return addJonaxelInteriorSuggestion({ coreApi, dispatch, rootState, props });
    });

  return isMobile() ? (
    <InteriorSuggestionsMobile
      selectedEntity={selectedEntity}
      selectedGroupId={selectedGroupId}
      suggestionsForSelectedFrame={interiorSuggestions}
      handleClick={handleClick}
      message="storageone.paxSelectFrameInSceneChatBubbleMessage"
      image={SelectInSceneImage}
      noneOptionForInteriorSuggestions={noneOptionForInteriorSuggestions}
    />
  ) : (
    <InteriorSuggestionsDesktop
      selectedEntity={selectedEntity}
      selectedGroupId={selectedGroupId}
      suggestionsForSelectedFrame={interiorSuggestions}
      handleClick={handleClick}
      message="storageone.paxSelectFrameInSceneChatBubbleMessage"
      image={SelectInSceneImage}
      noneOptionForInteriorSuggestions={noneOptionForInteriorSuggestions}
    />
  );
}
